import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { dashboardService } from '../services/dashboard.service';

interface SalesRep {
  id: string;
  name: string;
  department: string;
  pipelineValue: number;
  dealCount: number;
  target: number;
  status: string;
  // Timeline-specific pipelines (cumulative)
  march2026?: {
    total: number;
    dealCount: number;
  };
  fy26?: {
    total: number;
    dealCount: number;
  };
  fy27?: {
    total: number;
    dealCount: number;
  };
}

export const IndividualDashboardsList: React.FC = () => {
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getTeamSummary();
        // Handle the actual API response structure and map to SalesRep interface
        if (data && data.teamMetrics && Array.isArray(data.teamMetrics)) {
          const mappedReps = data.teamMetrics.map((rep: any) => ({
            id: rep.id,
            name: rep.fullName, // Map fullName to name
            department: rep.department,
            pipelineValue: rep.pipeline?.total || 0, // Map pipeline.total to pipelineValue
            dealCount: rep.dealCounts?.total || 0, // Map dealCounts.total to dealCount
            target: rep.target,
            status: rep.status,
            // Timeline-specific pipelines (already correctly named)
            march2026: rep.march2026,
            fy26: rep.fy26,
            fy27: rep.fy27,
          }));
          setSalesReps(mappedReps);
        } else {
          // If teamMetrics doesn't exist, set empty array
          setSalesReps([]);
        }
      } catch (err) {
        setError('Failed to load sales representatives');
        console.error(err);
        setSalesReps([]); // Set empty array on error
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading sales representatives...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-text-primary">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="max-w-full mx-auto px-12 py-6">
          <h1 className="text-3xl font-bold text-primary">👤 Individual Sales Dashboards</h1>
          <p className="text-text-secondary mt-1">
            Detailed view for each sales representative with their complete pipeline
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-12 py-8">
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Sales Representatives
          </h2>
          {salesReps.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-text-muted text-lg">No sales representatives found.</p>
              <p className="text-text-muted text-sm mt-2">Please check back later or contact your administrator.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {salesReps.map((rep) => {
              const statusIcon =
                rep.status === 'ON_TRACK' || rep.status === 'AHEAD'
                  ? '✓'
                  : rep.status === 'NEEDS_ATTENTION'
                  ? '⚠'
                  : '✗';

              const statusColor =
                rep.status === 'ON_TRACK' || rep.status === 'AHEAD'
                  ? 'text-success'
                  : rep.status === 'NEEDS_ATTENTION'
                  ? 'text-warning'
                  : 'text-danger';

              return (
                <Link key={rep.id} to={`/individual/${rep.id}`}>
                  <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-4xl">🎯</div>
                      <span className={`text-2xl ${statusColor}`}>{statusIcon}</span>
                    </div>

                    <h3 className="font-semibold text-primary text-xl mb-2">{rep.name}</h3>
                    <p className="text-sm text-text-muted mb-4">{rep.department}</p>

                    <div className="space-y-3">
                      {/* Deal Count Display */}
                      <div className="text-center py-6">
                        <div className="text-6xl font-bold text-primary mb-2">
                          {rep.dealCount}
                        </div>
                        <div className="text-sm text-text-muted font-medium">
                          Active Deals
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-border">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusColor} bg-opacity-20`}
                        style={{
                          backgroundColor:
                            rep.status === 'ON_TRACK' || rep.status === 'AHEAD'
                              ? 'rgba(16, 185, 129, 0.2)'
                              : rep.status === 'NEEDS_ATTENTION'
                              ? 'rgba(245, 158, 11, 0.2)'
                              : 'rgba(239, 68, 68, 0.2)',
                        }}
                      >
                        {rep.status.replace('_', ' ')}
                      </span>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
          )}
        </section>

        {/* Back to Home */}
        <section className="mt-8">
          <Link to="/dashboard">
            <Card className="hover:border-primary transition-colors cursor-pointer">
              <div className="flex items-center gap-4">
                <span className="text-2xl">←</span>
                <div>
                  <h3 className="font-semibold text-primary">Back to Dashboard Home</h3>
                  <p className="text-sm text-text-muted">Return to main dashboard</p>
                </div>
              </div>
            </Card>
          </Link>
        </section>
      </main>

      {/* Footer */}
      <footer className="mt-12 border-t border-border bg-surface">
        <div className="max-w-full mx-auto px-12 py-4">
          <p className="text-center text-sm text-text-muted">
            Individual Sales Dashboards © 2025
          </p>
        </div>
      </footer>
    </div>
  );
};
